import { FC, SyntheticEvent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Location, useLocation, useNavigate, useParams } from 'react-router-dom'
import { isUndefined } from 'lodash'
import { FormInput } from '@microservices/wiskey-react-components'
import WarningIcon from '@mui/icons-material/Warning'
import { Box, Button, Grid, Typography } from '@mui/material'
import { CellContext } from '@tanstack/react-table'

import { UpdateSettingsPopup } from '@pages/ConfiguredEntity/components/UpdateSettings/components/UpdateSettingsPopup/UpdateSettingsPopup'
import {
  getInitialPropertiesForRequest,
  getPropertiesForUpdate,
} from '@pages/Parameters/helpers/getPropertiesForUpdate'

import { getDataFromState, getStateFromData } from '@components/CommandParameters/helpers'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { ConfigRestrictions } from '@components/ConfigRestrictions/ConfigRestrictions'
import { DataSource } from '@components/DataSource'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'
import { HeaderGroupParameters } from '@components/HeaderGroupParameters'
import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { NewModel } from '@components/NewModel'
import { SwitchTable } from '@components/SwitchTable'
import { Tab, TabPanel, Tabs } from '@components/Tabs'
import { INITIAL_TEXT_STATE as defaultTextSettings, TextParams } from '@components/TextSettings'

import { useFetchContextVariablesByIdNoCacheMutation } from '@redux/api'
import { useUpdateLocalParameterPropertyWithViewResetMutation } from '@redux/api/parameters.api'
import { setStep } from '@redux/reducers/stepper.reducer'

import { useAppDispatch, usePrompt } from '@hooks'
import {
  getAvailableActionEvents,
  getColumnParametersFromArray,
  getDefaultValues,
  getLocalStorageContext,
  getParametersForCreation,
} from '@helpers'
import {
  DATAGRID_HEADER_WINDOW_HEIGHT,
  DEFAULT_TIME_IN_SECONDS_FOR_UPDATE_INTERVAL,
  ENTITY,
  ENTITY_COMMAND_TYPE,
  GENERATOR_INPUT_TYPE,
  MODAL_TYPE,
  MODEL_TYPE,
  REGEX,
  ROUTES,
  UPDATE_TYPE,
} from '@constants'

import { PARAMETER_SHOW_SA, PARAMETER_SHOW_SA_VALUES } from '../../../../constants/parameterShowSA'
import {
  AutocompleteOption,
  ConfiguredColumn,
  EntityType,
  FormValues,
  GETEntityColumn,
  ShareActionType,
} from '../../../../types'
import { PageContext } from '../../EntityCreateOrEdit'
import { ParametersModal } from '../ParametersModal'

import { Prefilter } from './components'

type EntitySuccessCallbackParams = {
  entityData?: EntityType
  entityColumnHeaderData: TextParams | undefined
  entityColumnTextData: TextParams | undefined
}

type EntityOnSubmitType = {
  data: FormValues
  isUpdate?: boolean
  entityColumnHeaderData?: TextParams
  entityColumnTextData?: TextParams
}

export const EntityConfiguration: FC = () => {
  const [currentTab, setCurrentTab] = useState('0')
  const [isOpenParameters, setOpenParameters] = useState(false)
  const [isHeaderGroupExpanded, setIsHeaderGroupExpanded] = useState(false)
  const {
    onShowDialog,
    deleteColumn,
    deleteAction,
    mainTable,
    actionTable,
    objects,
    onSetObjectCode,
    onSetModelCode,
    currentEntity,
    onCancel,
    addEntity,
    isSuccessAddModel,
    entityIsLoading,
    bindingValuesActionEvents,
    entityCreateLoading,
    entityCode,
    showDialog,
    updateColumnOrder,
    updateVisibilityColumn,
    watchUseParameters,
    watchQuickSearch,
    watchWrapRowText,
    watchHeaderHeight,
    watchHeaderGroupHeight,
    watchWrapHeaderText,
    methods: parametersMethods,
    modalType,
    handleUpdateColumnTitle,
    handleCreateAllColumns,
    handleDeleteAllColumns,
    createAllColumnsLoading,
    deleteAllColumnsLoading,
    createMandatoryColumnsLoading,
    watchParameterShowSA,
    isValidCreateOrEditForm,
    entityType,
  } = useContext(PageContext)

  const defaultValues: FormValues = getDefaultValues()
  const methods = useForm<FormValues>({ defaultValues })
  const {
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, defaultValues: formValues },
    getValues,
    setValue,
  } = methods
  const watchedObject = watch('object')
  const watchedPrefilter = watch('prefilter')
  const watchedUpdateSetting = watch('updateSetting')
  const watchedEditable = watch('editable')
  const watchMultiselection = watch('multiselection')
  const watchedColumnGroup = watch('columnGroups')

  const { code } = useParams()
  const location = useLocation() as Location & { state?: { code?: string } }
  const copyCode = location.state?.code
  const isEdit = !!code
  const isFormLoading = isEdit && entityIsLoading && !copyCode
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [isUpdateSettingsOpen, setIsUpdateSettingsOpen] = useState(false)

  const filteredActionEvents = getAvailableActionEvents(bindingValuesActionEvents, actionTable)
  const isCreate = [
    ROUTES.CONFIG_VIEWS_CREATE,
    ROUTES.CONFIG_LIST_CONTROLS_CREATE,
    ROUTES.CONFIG_DROP_DOWN_ENTITY_CREATE,
  ].includes(location.pathname)
  const isLeaveEdit = isDirty && !entityCreateLoading && !!currentEntity && !isCreate
  const isLeaveCreate = isDirty && isCreate && !entityCode
  const context = getLocalStorageContext()

  const columnsOptions: AutocompleteOption[] = useMemo(
    () => currentEntity?.columns?.map(({ code }) => ({ id: code, label: code })) || [],
    [currentEntity?.columns]
  )

  const [fetchVariables, { data: variables, isLoading: isVariablesLoading }] =
    useFetchContextVariablesByIdNoCacheMutation()

  const [updateParameterProperty, { isLoading: isParametersLoading }] =
    useUpdateLocalParameterPropertyWithViewResetMutation()

  usePrompt({ when: isLeaveEdit || isLeaveCreate })

  const variableOptions = useMemo(
    () => variables?.variables.map(({ id, variableTitle: label }) => ({ id, label })) || [],
    [variables?.variables]
  )

  const selectedObjectCode = useMemo(() => (watchedObject ? watchedObject.id : ''), [watchedObject])

  useEffect(() => {
    if (context && !isVariablesLoading) {
      fetchVariables(context?.id)
        .unwrap()
        .catch(error => {
          if (error.status === 404) {
            localStorage.removeItem('context')
            navigate(ROUTES.CHOOSE_CONTEXT)
          }
        })
    }
  }, [context?.id])

  useEffect(() => {
    dispatch(setStep(0))
  }, [location])

  useEffect(() => {
    if (currentEntity && objects) {
      const object = objects?.find(obj => obj.code === currentEntity.objectCode)

      onSetModelCode(currentEntity.code)
      const commands = currentEntity.commands.map(command => ({
        ...command,
        type: {
          id: command.type,
          label: command.type,
        },
        name: {
          id: command.name,
          label: command.name,
        },
        sourceCommand: {
          id: command.objectCode,
          label: command.objectTitle,
        },
        sourceType: {
          id: command.resource,
          label: command.resource,
        },
        parameters: getStateFromData(command.parameters),
        body: getStateFromData(command.body),
      }))

      reset({
        ...currentEntity,
        updateSetting: {
          type: currentEntity.updateSetting?.type || UPDATE_TYPE.WEBSOCKET,
          interval:
            Number(currentEntity.updateSetting?.interval) ||
            DEFAULT_TIME_IN_SECONDS_FOR_UPDATE_INTERVAL,
        },
        commands,
        object: object ? { id: object.code, label: object.title } : null,
        update: t(
          `updateSettings.${(
            currentEntity.updateSetting?.type || UPDATE_TYPE.WEBSOCKET
          ).toLowerCase()}`
        ),
        columnGroups: currentEntity.columnHeaders,
      })

      return
    }

    onSetModelCode('')
  }, [currentEntity, objects])

  useEffect(() => {
    if (watchedObject && objects) {
      const currentObj = objects.filter(obj => obj.title === watchedObject.label)
      if (currentObj.length) {
        onSetObjectCode?.(currentObj[0].code)
      }
    }
  }, [watchedObject])

  useEffect(() => {
    if (!isOpenParameters) {
      parametersMethods.reset({
        useParameters: Boolean(currentEntity?.useLocalParameters),
        hasQuickSearch: Boolean(currentEntity?.hasQuickSearch),
        hasWrapRowText: Boolean(currentEntity?.hasWrapRowText),
        hasWrapHeaderText: Boolean(currentEntity?.hasWrapHeaderText),
        headerHeight: !currentEntity?.headerHeight
          ? DATAGRID_HEADER_WINDOW_HEIGHT
          : currentEntity?.headerHeight,
        headerGroupHeight: !currentEntity?.headerGroupHeight
          ? DATAGRID_HEADER_WINDOW_HEIGHT
          : currentEntity?.headerGroupHeight,
        parameterShowSA: currentEntity?.parameterShowSA
          ? {
              id: `${currentEntity?.parameterShowSA}`,
              label:
                PARAMETER_SHOW_SA[currentEntity?.parameterShowSA as keyof typeof PARAMETER_SHOW_SA],
            }
          : null,
      })
    }
  }, [isOpenParameters, currentEntity])

  const handleCloseParameters = () => {
    setOpenParameters(false)
  }

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const handleEdit = (id: number | string, isAction?: boolean) => {
    if (id) {
      onShowDialog(MODAL_TYPE.EDIT, id, isAction)
    }
  }

  const handleCreateEntitySuccess = ({
    entityData,
    entityColumnHeaderData,
    entityColumnTextData,
  }: EntitySuccessCallbackParams) => {
    const { bgFontColor, formats, ...otherDefaultTextSettings } = defaultTextSettings
    const textSettingsData = {
      ...otherDefaultTextSettings,
      isBold: String(Boolean(formats.find(format => format === 'bold'))),
      isItalic: String(Boolean(formats.find(format => format === 'italic'))),
      fontFamily: defaultTextSettings.fontFamily.label,
    }
    const columnHeaderData = entityColumnHeaderData ?? textSettingsData
    const columnTextData = entityColumnTextData ?? {
      ...textSettingsData,
      bgFontColor,
    }

    if (!entityData?.parameters) {
      return
    }

    const { headerParamId, textParamId } = getColumnParametersFromArray(entityData.parameters)

    if (!textParamId || !headerParamId) {
      return
    }

    const propertiesForCreation = [
      ...getInitialPropertiesForRequest({
        textData: columnHeaderData,
        paramId: headerParamId,
      }),
      ...getInitialPropertiesForRequest({
        textData: columnTextData,
        paramId: textParamId,
      }),
    ]

    updateParameterProperty(propertiesForCreation)

    reset(defaultValues)
  }

  const handleUpdateColumn = ({
    entityData,
    entityColumnHeaderData,
    entityColumnTextData,
  }: EntitySuccessCallbackParams) => {
    if (!entityColumnHeaderData || !entityColumnTextData || !entityData?.parameters) {
      return
    }

    const { headerProperties, textProperties, headerParamId, textParamId } =
      getColumnParametersFromArray(entityData.parameters)

    if (!headerProperties || !textProperties || !headerParamId || !textParamId) {
      return
    }

    if (headerProperties.length === 0 && textProperties.length === 0) {
      handleCreateEntitySuccess({
        entityData,
        entityColumnHeaderData,
        entityColumnTextData,
      })

      return
    }

    const propertiesForUpdate = [
      ...getPropertiesForUpdate(entityColumnHeaderData, headerProperties, headerParamId),
      ...getPropertiesForUpdate(entityColumnTextData, textProperties, textParamId),
    ]

    updateParameterProperty(propertiesForUpdate)

    handleCloseParameters()
  }

  const onSubmit = ({
    data,
    isUpdate = false,
    entityColumnHeaderData,
    entityColumnTextData,
  }: EntityOnSubmitType) => {
    const { id, method, dataType, code, title, object, commands: commandOptions, prefilter } = data

    const columnHeaderCode = `column_header_styles_${code}`
    const columnTextCode = `column_text_styles_${code}`
    const commands = commandOptions
      .filter(command => command.type && command.name && command.sourceCommand)
      .map(command => ({
        ...command,
        type: (command.type as AutocompleteOption<ENTITY_COMMAND_TYPE>).id,
        name: (command.name as AutocompleteOption<string>).id,
        objectCode: (command.sourceCommand as AutocompleteOption<string>).id,
        objectTitle: (command.sourceCommand as AutocompleteOption<string>)?.label,
        resource: command.sourceType?.id,
        parameters: getDataFromState(command.parameters),
        body: getDataFromState(command.body),
      }))

    addEntity?.({
      id,
      code,
      title,
      commands,
      editable: watchedEditable,
      multiselection: watchMultiselection,
      objectCode: object?.id || '',
      objectTitle: object?.label || '',
      useLocalParameters: !isUndefined(watchUseParameters)
        ? watchUseParameters
        : currentEntity?.useLocalParameters ?? false,
      hasQuickSearch: !isUndefined(watchQuickSearch)
        ? watchQuickSearch
        : currentEntity?.hasQuickSearch ?? true,
      hasWrapRowText: !isUndefined(watchWrapRowText)
        ? watchWrapRowText
        : currentEntity?.hasWrapRowText ?? false,
      headerHeight: currentEntity ? watchHeaderHeight : DATAGRID_HEADER_WINDOW_HEIGHT,
      headerGroupHeight: currentEntity ? watchHeaderGroupHeight : DATAGRID_HEADER_WINDOW_HEIGHT,
      hasWrapHeaderText: !isUndefined(watchWrapHeaderText)
        ? watchWrapHeaderText
        : currentEntity?.hasWrapHeaderText ?? false,
      parameterShowSA: watchParameterShowSA?.id ?? PARAMETER_SHOW_SA_VALUES[PARAMETER_SHOW_SA.VIEW],
      prefilter,
      updateSetting: watchedUpdateSetting,
      parameters: [],
      // Создание параметров
      ...(isUpdate && !currentEntity?.parameters?.length
        ? { parameters: getParametersForCreation(columnHeaderCode, columnTextCode) }
        : {}),
      columnHeaders: watchedColumnGroup,
    })
      .then(entityData => {
        if (isUpdate) {
          handleUpdateColumn({
            entityData,
            entityColumnHeaderData,
            entityColumnTextData,
          })
        }

        parametersMethods.reset({
          useParameters: entityData.useLocalParameters,
          hasQuickSearch: entityData.hasQuickSearch,
          hasWrapRowText: entityData.hasWrapRowText,
          hasWrapHeaderText: entityData.hasWrapHeaderText,
          headerHeight: entityData.headerHeight,
          headerGroupHeight: entityData.headerGroupHeight,
          parameterShowSA: entityData?.parameterShowSA
            ? {
                id: entityData?.parameterShowSA,
                label:
                  PARAMETER_SHOW_SA[
                    currentEntity?.parameterShowSA as keyof typeof PARAMETER_SHOW_SA
                  ],
              }
            : null,
        })
      })
      .catch(() => {
        onSetModelCode('')
      })
    onSetModelCode(code)
    setIsHeaderGroupExpanded(false)
  }

  const handleDropChange = (rows: GETEntityColumn[]) => {
    const result = rows.map((el, i) => ({ ...el, order: i }))

    if (JSON.stringify(result) !== JSON.stringify(mainTable.rows)) {
      updateColumnOrder(result.map(el => ({ id: Number(el.id), order: el.order })))
    }

    return result
  }

  const handleHide = (params: CellContext<ConfiguredColumn, unknown>, visibility: boolean) => {
    updateVisibilityColumn({ id: Number(params.row.original.id), visibility })
  }

  const handleOpenParameters = () => setOpenParameters(true)

  const handleSaveParameters = (
    entityColumnHeaderData: TextParams,
    entityColumnTextData: TextParams
  ) => {
    onSubmit({ data: getValues(), isUpdate: true, entityColumnHeaderData, entityColumnTextData })
  }

  const handleSwitchUpdateType = (newUpdateType: UPDATE_TYPE, newUpdateInterval: number) => {
    setValue(
      'updateSetting',
      {
        type: newUpdateType,
        interval: newUpdateInterval,
      },
      { shouldDirty: true }
    )
  }

  const modelType = isEdit ? 'editModel' : 'newModel'
  const isDropDownEntity = entityType === ENTITY.DROP_DOWN_ENTITY

  return (
    <Box data-component='EntityConfiguration' sx={{ width: '100%' }}>
      <form>
        <FormProvider {...methods}>
          <EditConfigScriptDialog />
          <Grid container px={1} py={0.5}>
            <Grid container alignItems='center' justifyContent={'space-between'}>
              <Typography py={0.5} variant='h5'>
                {t(`configEntity.${entityType}.${modelType}`)}
              </Typography>
              <Box alignItems='center' display={'flex'} justifyContent={'space-between'}>
                {currentEntity?.id && <EditConfigMenu configId={currentEntity?.id} />}
                <Button
                  variant='outlined'
                  disabled={
                    !currentEntity || entityCreateLoading || isParametersLoading || entityIsLoading
                  }
                  onClick={handleOpenParameters}
                >
                  {t(`configEntity.${entityType}.parameters.title`)}
                </Button>
              </Box>
            </Grid>
            <Grid container display='flex' pb={0.5} spacing={2}>
              <Grid item md={6} xs={12}>
                <Typography py={0.5} variant='h5'>
                  {t('viewsCreate.general')}
                </Typography>
                <NewModel entityType={entityType} loading={isFormLoading} />
                <Prefilter
                  keyWordList={variableOptions}
                  value={watchedPrefilter}
                  keyWordObj={{
                    pattern: REGEX.GLOBAL_VARIABLE,
                    alias: 'global',
                  }}
                  onChange={value => setValue('prefilter', value, { shouldDirty: true })}
                />
                <HeaderGroupParameters
                  isExpanded={isHeaderGroupExpanded}
                  options={columnsOptions}
                  onToggleExpanded={setIsHeaderGroupExpanded}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <DataSource
                  isDisabledObject={!!code || !!copyCode}
                  isLoading={isFormLoading}
                  objects={objects}
                />
                <FormInput
                  ref={anchorRef}
                  readOnly
                  inputType={GENERATOR_INPUT_TYPE.SELECT}
                  label={t('label.update')}
                  name={'update'}
                  value={t(`updateSettings.${watchedUpdateSetting.type.toLowerCase()}`)}
                  sx={{
                    '& .MuiSelect-select': {
                      flexGrow: 1,
                      width: 0,
                    },
                  }}
                  onClick={() => setIsUpdateSettingsOpen(true)}
                />
                <UpdateSettingsPopup
                  anchorRef={anchorRef}
                  isUpdateSettingsOpen={isUpdateSettingsOpen}
                  position={'bottom-end'}
                  sx={{ mt: 0, mr: 0, width: anchorRef.current?.clientWidth }}
                  updateType={watchedUpdateSetting.type}
                  updateInterval={
                    watchedUpdateSetting.interval || DEFAULT_TIME_IN_SECONDS_FOR_UPDATE_INTERVAL
                  }
                  onSwitchUpdateType={handleSwitchUpdateType}
                  onToggleUpdateSettings={setIsUpdateSettingsOpen}
                />
                {!isDropDownEntity && (
                  <Box alignItems={'flex-start'} display={'flex'} flexDirection={'column'}>
                    <FormInput
                      inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                      label={t('viewsCreate.editable.label')}
                      name={'editable'}
                    />
                    <FormInput
                      inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                      label={t('viewsCreate.multiselection.label')}
                      name={'multiselection'}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
            <Grid item md>
              <CommandPickerController
                isAnotherObjectWithRequestData
                commandName='name'
                objectCode={selectedObjectCode}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </form>
      <ParametersModal
        isLoading={entityCreateLoading}
        isShow={isOpenParameters}
        isValidForm={isValidCreateOrEditForm}
        onClose={handleCloseParameters}
        onSave={handleSaveParameters}
      />
      <Grid container justifyContent={'flex-end'}>
        <GeneralButtons
          cancelText={t('viewOrFormCreate.cancelBtnText')}
          isLoading={entityCreateLoading || entityIsLoading}
          saveDisabled={!isDirty || entityCreateLoading || entityIsLoading}
          saveText={t('viewOrFormCreate.saveBtnText')}
          onCancel={onCancel}
          onSave={handleSubmit(data => {
            onSubmit({ data })
          })}
        />
      </Grid>
      <Box sx={{ pl: 1, pt: 0 }}>
        {!isDropDownEntity && (
          <Box
            alignItems={'center'}
            color={'#c0a80b'}
            display={'flex'}
            gap={1}
            justifyContent={'flex-start'}
          >
            <WarningIcon />
            <Typography>{t(`configEntity.warning`)}</Typography>
          </Box>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab
              label={t(`configEntity.${entityType}.columns.title`)}
              value={'0'}
              sx={{
                pl: 0,
              }}
            />
            {!isDropDownEntity && (
              <Tab
                label={t(`configEntity.${entityType}.actions.title`)}
                value={'1'}
                sx={{
                  pl: 0,
                }}
              />
            )}
            <Tab
              label={t(`configEntity.restrictions`)}
              value={'2'}
              sx={{
                pl: 0,
              }}
            />
          </Tabs>
        </Box>
        <TabPanel index={'0'} value={currentTab}>
          <SwitchTable
            disablePagination
            isCrud
            showVisibilityColumn
            btnDisabled={!isEdit && !isSuccessAddModel}
            btnText={t(`configEntity.${entityType}.columns.addButton`)}
            columns={mainTable.columns}
            createAllBtnText={t('configEntity.allEntities.createAll')}
            createAllRequiredBtnText={t('configEntity.allEntities.createAllMandatory')}
            deleteAllBtnText={t('configEntity.deleteAll')}
            isCreateAllLoading={createAllColumnsLoading}
            isCreateMandatoryLoading={createMandatoryColumnsLoading}
            isDeleteAllLoading={deleteAllColumnsLoading}
            loading={entityIsLoading}
            rows={mainTable.rows}
            showActionsColumn={!copyCode}
            showDialog={showDialog}
            showSearch={true}
            type={entityType as ShareActionType}
            updateRow={handleUpdateColumnTitle}
            useDragAndDrop={false}
            containerSx={{
              '.MuiTableBody-root > div': {
                height: '400px !important',
              },
            }}
            onCreateAllEntityFields={handleCreateAllColumns}
            onDelete={deleteColumn}
            onDeleteAllEntityFields={handleDeleteAllColumns}
            onDropChange={handleDropChange}
            onEdit={params => handleEdit(params.row.original.id)}
            onHide={handleHide}
            onShowDialog={onShowDialog}
          />
        </TabPanel>
        {!isDropDownEntity ? (
          <TabPanel index={'1'} value={currentTab}>
            <SwitchTable
              disablePagination
              isCrud
              btnDisabled={!isEdit && !isSuccessAddModel}
              btnText={t(`configEntity.${entityType}.actions.addButton`)}
              columns={actionTable.columns}
              loading={entityIsLoading}
              rows={actionTable.rows}
              showActionsColumn={!copyCode}
              showDialog={showDialog}
              type={MODEL_TYPE.VIEW_ACTION}
              containerSx={{
                '.MuiTableBody-root > div': {
                  height: '400px !important',
                },
              }}
              onDelete={deleteAction}
              onEdit={params => handleEdit(params.row.original.id, true)}
              onShowDialog={onShowDialog}
            />
          </TabPanel>
        ) : (
          <></>
        )}
        <TabPanel index={'2'} value={currentTab}>
          <ConfigRestrictions
            hiddenColumns={isDropDownEntity ? ['show'] : []}
            rows={mainTable.rows}
            viewId={currentEntity?.id}
          />
        </TabPanel>
      </Box>
    </Box>
  )
}
